/**
 * Cookie-consent write path + helpers shared by the banner, the footer settings
 * link, and the GTM bootstrap script in app/layout.tsx (which reads the same
 * cookie before first paint to set Google Consent Mode v2 defaults).
 *
 * The read-only state (readStoredConsent, CONSENT_VERSION, ConsentChoice) lives
 * in ./consent-state so read-only consumers (lib/posthog-events.ts) avoid
 * importing this module's analytics-SDK write path and the cycle it would form.
 * Re-exported here so existing import sites keep working.
 */

import {
  CONSENT_COOKIE,
  CONSENT_VERSION,
  type ConsentChoice,
} from "./consent-state";
import { setPosthogConsent } from "./posthog-events";

export { readStoredConsent } from "./consent-state";
export type { ConsentChoice } from "./consent-state";

export const OPEN_COOKIE_SETTINGS_EVENT = "sq:open-cookie-settings";

const COOKIE_MAX_AGE_SECONDS = 180 * 24 * 60 * 60;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    __sqLoadGtm?: () => void;
  }
}

export function applyConsent(choice: ConsentChoice): void {
  const record = {
    v: CONSENT_VERSION,
    ts: new Date().toISOString(),
    analytics: choice.analytics,
    marketing: choice.marketing,
  };
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${CONSENT_COOKIE}=${encodeURIComponent(
    JSON.stringify(record),
  )}; path=/; max-age=${COOKIE_MAX_AGE_SECONDS}; SameSite=Lax${secure}`;

  const granted = (flag: boolean) => (flag ? "granted" : "denied");
  window.gtag?.("consent", "update", {
    ad_storage: granted(choice.marketing),
    ad_user_data: granted(choice.marketing),
    ad_personalization: granted(choice.marketing),
    analytics_storage: granted(choice.analytics),
  });
  window.dataLayer?.push({ event: "sq_consent_update" });

  // PostHog is an analytics tool: opt it in/out with the analytics category.
  // No-op until posthog-js has loaded (see lib/posthog-events.ts).
  setPosthogConsent(choice.analytics);

  // Only relevant when GTM_LOAD=consent deferred the container; the loader
  // itself is a no-op once GTM is on the page.
  if (choice.analytics || choice.marketing) window.__sqLoadGtm?.();
}

export function openCookieSettings(): void {
  window.dispatchEvent(new CustomEvent(OPEN_COOKIE_SETTINGS_EVENT));
}
